import { humanizeMinutes } from '@lume/shared/worklist';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useResolvePendency, useUpdatePendency } from '../../../hooks/use-pendencies';
import { ApiError } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';
import { useConversationPromise } from '../hooks/use-conversation-promise';

import type { ConversationDetail } from '../../../lib/types';

/**
 * CommitmentBanner — S2 PR-b (Corrente de Compromissos), "pouso com
 * propósito" (design §4). Ao abrir a Conversa com um compromisso
 * ATENDENTE/IA→CLIENTE ainda ABERTO, este banner é a 1ª coisa visível
 * (renderizado no topo da coluna, ACIMA do histórico — vide `ConversationView`).
 *
 * Self-contido (molde `PlaybookTransitionPendingBanner`/`AiConductingBanner`):
 * gateia a capability + o fetch aqui dentro (`useConversationPromise`) — o
 * caller só passa a `conversation`.
 *
 * 3 gestos, todos via endpoints EXISTENTES de Pendency (If-Match):
 *   1. "Cumprido ✓"          → resolve (outcome=success)
 *   2. "Não era compromisso" → cancela (status=cancelled) — alimenta
 *      `commitment.dismissed` no Engine (métrica de falsificação, design §3).
 *   3. "Ajustar prazo"       → date-picker inline → PATCH deadline — alimenta
 *      `commitment.corrected`.
 *
 * Falha de mutação NUNCA é silenciosa (revisão adversarial — Important #1):
 * If-Match stale (412/409 — outra aba resolveu, ou `version` envelheceu na
 * janela pós-mutação) ganha `onError` com (a) invalidação imediata do prefixo
 * `['pendencies']` → o banner refetcha e re-renderiza com a versão fresca
 * (ou some, se o compromisso já foi fechado pela outra aba) + (b) toast de
 * erro no padrão do app (molde `CommitmentEditModal`/`ConversationBookingModal`).
 *
 * Tom: vencido = borda/fundo warning; no prazo = neutro (design §4).
 */
export function CommitmentBanner({
  conversation,
}: {
  conversation: ConversationDetail;
}): JSX.Element | null {
  const { promise } = useConversationPromise(conversation);
  const resolvePendency = useResolvePendency();
  const updatePendency = useUpdatePendency();
  const queryClient = useQueryClient();
  const [adjusting, setAdjusting] = useState(false);
  const [newDeadline, setNewDeadline] = useState('');

  /**
   * onError comum aos 3 gestos: refetch imediato (versão fresca no banner) +
   * toast de erro (nunca falha silenciosa). 409/412 = If-Match stale (outra
   * aba mexeu) → copy dedicada; demais erros → mensagem do servidor.
   */
  const handleMutationError = (err: unknown): void => {
    void queryClient.invalidateQueries({ queryKey: ['pendencies'] });
    const stale = err instanceof ApiError && (err.status === 409 || err.status === 412);
    useToastStore.getState().push({
      variant: 'error',
      title: 'Não foi possível aplicar',
      description: stale
        ? 'O compromisso foi atualizado em outra janela — recarregamos os dados; tente de novo.'
        : err instanceof Error
          ? err.message
          : 'Erro desconhecido',
      durationMs: 6000,
    });
  };

  if (promise === null) return null;

  const busy = resolvePendency.isPending || updatePendency.isPending;

  const metaParts: string[] = [];
  const promisedMs = Date.parse(promise.promisedAtIso);
  if (Number.isFinite(promisedMs)) {
    metaParts.push(
      `há ${humanizeMinutes(Math.max(0, Math.floor((Date.now() - promisedMs) / 60000)))}`,
    );
  }
  if (promise.dueAtIso !== null) {
    const dueMs = Date.parse(promise.dueAtIso);
    if (Number.isFinite(dueMs)) {
      const diffMinutes = Math.round((dueMs - Date.now()) / 60000);
      metaParts.push(
        promise.overdue
          ? `venceu há ${humanizeMinutes(Math.max(0, -diffMinutes))}`
          : `até ${humanizeMinutes(Math.max(0, diffMinutes))}`,
      );
    }
  }
  if (promise.customerReturns > 0) {
    metaParts.push(`cliente voltou ${promise.customerReturns.toString()}×`);
  }

  const handleFulfilled = (): void => {
    resolvePendency.mutate(
      {
        pendencyId: promise.pendencyId,
        expectedVersion: promise.version,
        body: { outcome: 'success' },
      },
      { onError: handleMutationError },
    );
  };

  const handleDismiss = (): void => {
    updatePendency.mutate(
      {
        pendencyId: promise.pendencyId,
        expectedVersion: promise.version,
        body: { status: 'cancelled' },
      },
      { onError: handleMutationError },
    );
  };

  const handleAdjustSubmit = (): void => {
    const iso = localInputToIso(newDeadline);
    if (iso === null) return;
    updatePendency.mutate(
      {
        pendencyId: promise.pendencyId,
        expectedVersion: promise.version,
        body: { deadline: iso },
      },
      { onSuccess: () => setAdjusting(false), onError: handleMutationError },
    );
  };

  return (
    <div
      className="flex flex-col gap-2 border-b px-6 py-3"
      style={{
        borderColor: promise.overdue ? 'var(--warning-border)' : 'var(--border-default)',
        background: promise.overdue ? 'var(--warning-bg)' : 'var(--bg-surface-2)',
        color: promise.overdue ? 'var(--warning-fg)' : 'var(--fg-2)',
      }}
      role="status"
      aria-live="polite"
      data-testid="commitment-banner"
    >
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="text-base">
          {promise.overdue ? '⏰' : '🤝'}
        </span>
        <div className="flex-1 text-sm">
          <span className="font-semibold">Você prometeu: {promise.what}</span>
          {metaParts.length > 0 && (
            <span className="ml-2 text-xs opacity-80">({metaParts.join(' · ')})</span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-ghost-mini"
          data-testid="commitment-banner-fulfilled"
          onClick={handleFulfilled}
          disabled={busy}
        >
          Cumprido ✓
        </button>
        <button
          type="button"
          className="btn-ghost-mini"
          data-testid="commitment-banner-dismiss"
          onClick={handleDismiss}
          disabled={busy}
        >
          Não era compromisso
        </button>
        {adjusting ? (
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={newDeadline}
              onChange={(e) => setNewDeadline(e.target.value)}
              style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}
              className="rounded border px-2 py-1 text-xs"
              aria-label="Novo prazo do compromisso"
              data-testid="commitment-banner-deadline-input"
            />
            <button
              type="button"
              className="btn-ghost-mini"
              data-testid="commitment-banner-deadline-confirm"
              onClick={handleAdjustSubmit}
              disabled={busy || newDeadline === ''}
            >
              Salvar prazo
            </button>
            <button
              type="button"
              className="btn-ghost-mini"
              onClick={() => {
                setAdjusting(false);
                setNewDeadline('');
              }}
              disabled={busy}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn-ghost-mini"
            data-testid="commitment-banner-adjust"
            onClick={() => setAdjusting(true)}
            disabled={busy}
          >
            Ajustar prazo
          </button>
        )}
      </div>
    </div>
  );
}

/** Valor de `datetime-local` → ISO (interpretado no fuso do navegador). `''`/inválido ⇒ null. */
function localInputToIso(local: string): string | null {
  if (local === '') return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
